import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "react-bootstrap";
import "../../../assets/css/gcstudent/bookdetailpage/BookDetailPage.css";
import AppContext from "../../../context/AppContext";
import axios from "axios";
import Swal from "sweetalert2";

/* ══════════════════════════════════════════════
   Confirm Reservation Page
   ✅ loggedInUser + userType seedha AppContext se
   ✅ authLoading khatam hone tak wait karte hain — warna refresh ya
      fresh-login redirect ke turant baad, agar context abhi update ho
      raha ho, to galti se user ko "/" pe bhej deta tha (race condition)
   ✅ available_copies (backend field) use ho raha hai — agar reservation
      ke dauran hi 0 ho jayen (kisi aur ne le liya), Confirm button
      disable ho jata hai aur backend rejection ka message bhi dikhta hai
   ✅ Book Configuration (fines, max issue days, reservation expiry hours,
      max books per student/staff) ab admin ke Settings > Book Config se
      public route ke zariye dynamically fetch hoti hai — Important Notice
      aur Reservation Policy dono is config ke hisaab se update hote hain,
      aur student/teacher ke liye policy alag dikhti hai (teacher ke liye
      koi fine line nahi, sirf uski apni capacity line)
══════════════════════════════════════════════ */
const ConfirmReservationPage = () => {
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [visible, setVisible] = useState(false);
  const [done, setDone] = useState(false);
  const [bookConfig, setBookConfig] = useState(null);

  const { loggedInUser, userType, authLoading, baseUrl } = useContext(AppContext);

  const isTeacher = userType === "teacher";
  const currentUser = loggedInUser;

  useEffect(() => {
    setTimeout(() => setVisible(true), 60);

    if (authLoading) return; // auto-login abhi verify ho raha hai — abhi decide mat karo

    const pendingBook = localStorage.getItem("pendingReservation");

    if (!pendingBook) {
      navigate("/");
      return;
    }

    setBook(JSON.parse(pendingBook));

    if (!loggedInUser?.id || (userType !== "student" && userType !== "teacher")) {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser, userType, authLoading]);

  // ✅ Book config (fines, days, expiry hours) — public route, koi token nahi chahiye
  useEffect(() => {
    axios
      .post(`${baseUrl}/general/bookConfig/getBookConfiguration`, {})
      .then((res) => {
        if (res.data.success && res.data.bookConfiguration) {
          setBookConfig(res.data.bookConfiguration);
        }
      })
      .catch((err) => {
        console.log(err);
        // config fetch fail ho to neeche diye gaye defaults use ho jayenge
      });
  }, [baseUrl]);

  // ✅ Config values — agar backend se abhi tak load nahi hui to sensible defaults
  const expiryHours = bookConfig?.reservation_expiry_hours ?? 24;
  const maxIssueDays = bookConfig?.max_issue_days ?? 14;
  const finePerDay = bookConfig?.fine_per_day ?? 10;
  const lostBookFine = bookConfig?.lost_book_fine ?? 200;
  const damagedBookFine = bookConfig?.damaged_book_fine ?? 50;
  const maxBooksStudent = bookConfig?.max_books_student ?? 4;
  const maxBooksStaff = bookConfig?.max_books_staff ?? 7;

  const availableCopies = book?.available_copies ?? book?.total_copies ?? 0;

  const handleConfirm = () => {
    if (availableCopies === 0) return; // extra guard, button is disabled anyway

    const token = localStorage.getItem("authToken");
    const formData = new FormData();

    formData.append(
      isTeacher ? "lbteacher_id" : "lbstudent_id",
      currentUser.id
    );
    formData.append("lbbook_id", book.id);
    formData.append("issuedby", isTeacher ? "teacher" : "student");
    formData.append("status", "reserved");

    const url = isTeacher
      ? `${baseUrl}/teacher/booking/newReservation`
      : `${baseUrl}/student/booking/newReservation`;

    axios
      .post(url, formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data.success === true) {
          Swal.fire({
            icon: "success",
            title: "Book Reserved",
            text: "You can receive your book from the library.",
            confirmButtonText: "Okay",
          });
          localStorage.removeItem("pendingReservation");
          setDone(true);
        } else {
          // ✅ Backend rejected (e.g. no copies available anymore) —
          // show the real reason instead of failing silently.
          Swal.fire({
            icon: "warning",
            title: "Reservation Unavailable",
            text: res.data.message || "This book could not be reserved right now.",
          });
        }
      })
      .catch((err) => {
        console.log(err);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Something went wrong. Please try again.",
        });
      });
  };

  const handleCancel = () => {
    localStorage.removeItem("pendingReservation");
    navigate(isTeacher ? "/teacher/dashboard" : "/student/dashboard");
  };

  const handleDashboard = () => {
    navigate(isTeacher ? "/teacher/dashboard" : "/student/dashboard");
  };

  if (authLoading || !book || !currentUser) return null;

  // ══════════════════════════════
  // Confirmed Screen
  // ══════════════════════════════
  if (done) {
    return (
      <div className={`gcStudent-resv-page ${visible ? "gcStudent-resv-page--in" : ""}`}>
        <Container fluid="lg" className="gcStudent-resv-container">
          <div className="gcStudent-resv-card">
            <div className="gcStudent-resv-success-icon">
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17l-5-5" stroke="#16a34a" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <h2 className="gcStudent-resv-heading">Reservation Confirmed!</h2>
            <p className="gcStudent-resv-subtext">
              Your reservation for <strong>"{book.title}"</strong> has been
              placed successfully. Please visit the library within{" "}
              <strong>{expiryHours} hours</strong> to collect your book.
            </p>

            <div className="gcStudent-resv-summary">
              <div className="gcStudent-resv-summary-row">
                <span className="gcStudent-resv-summary-label">Book</span>
                <span className="gcStudent-resv-summary-value">{book.title}</span>
              </div>
              <div className="gcStudent-resv-summary-row">
                <span className="gcStudent-resv-summary-label">Author</span>
                <span className="gcStudent-resv-summary-value">{book.author}</span>
              </div>
              <div className="gcStudent-resv-summary-row">
                <span className="gcStudent-resv-summary-label">
                  {isTeacher ? "Teacher" : "Student"}
                </span>
                <span className="gcStudent-resv-summary-value">
                  {currentUser?.fullName || currentUser?.name}
                </span>
              </div>
              <div className="gcStudent-resv-summary-row gcStudent-resv-summary-row--last">
                <span className="gcStudent-resv-summary-label">Status</span>
                <span className="gcStudent-resv-summary-value gcStudent-resv-summary-value--green">
                  ✓ Confirmed
                </span>
              </div>
            </div>

            <div className="gcStudent-resv-actions">
              <button className="gcStudent-resv-confirm-btn" onClick={handleDashboard}>
                Go to Dashboard
              </button>
              <button className="gcStudent-resv-cancel-btn" onClick={() => navigate("/")}>
                Back to Home
              </button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  // ══════════════════════════════
  // Confirm Screen
  // ══════════════════════════════
  return (
    <div className={`gcStudent-resv-page ${visible ? "gcStudent-resv-page--in" : ""}`}>
      <Container fluid="lg" className="gcStudent-resv-container">
        <div className="gcStudent-resv-card">
          <div className="gcStudent-resv-page-header">
            <div className="gcStudent-resv-page-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M4 19.5A2.5 2.5 0 016.5 17H20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" stroke="currentColor" strokeWidth="1.8"/>
              </svg>
            </div>
            <h2 className="gcStudent-resv-heading">Confirm Reservation</h2>
            <p className="gcStudent-resv-subtext">
              Please review the details below before confirming your reservation.
            </p>
          </div>

          <div className="gcStudent-resv-summary">
            <div className="gcStudent-resv-summary-row">
              <span className="gcStudent-resv-summary-label">Book</span>
              <span className="gcStudent-resv-summary-value">{book.title}</span>
            </div>
            <div className="gcStudent-resv-summary-row">
              <span className="gcStudent-resv-summary-label">Author</span>
              <span className="gcStudent-resv-summary-value">{book.author}</span>
            </div>
            <div className="gcStudent-resv-summary-row">
              <span className="gcStudent-resv-summary-label">
                {isTeacher ? "Teacher" : "Student"}
              </span>
              <span className="gcStudent-resv-summary-value">
                {currentUser?.fullName || currentUser?.name}
              </span>
            </div>
            <div className="gcStudent-resv-summary-row gcStudent-resv-summary-row--last">
              <span className="gcStudent-resv-summary-label">Available Copies</span>
              <span className={`gcStudent-resv-summary-value ${availableCopies === 0 ? "" : "gcStudent-resv-summary-value--green"}`}>
                {availableCopies}
              </span>
            </div>
          </div>

          {availableCopies === 0 && (
            <div className="gcStudent-resv-notice gcStudent-resv-notice--warning">
              <span className="gcStudent-resv-notice__icon">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#d97706" strokeWidth="1.8"/>
                  <path d="M12 8v4M12 16h.01" stroke="#d97706" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
              <div className="gcStudent-resv-notice__body">
                <span className="gcStudent-resv-notice__heading">No Copies Left</span>
                <p className="gcStudent-resv-notice__text">
                  All copies of this book have just been reserved by other users.
                  Please check back later or choose another book.
                </p>
              </div>
            </div>
          )}

          <div className="gcStudent-resv-notice gcStudent-resv-notice--warning">
            <span className="gcStudent-resv-notice__icon">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#d97706" strokeWidth="1.8"/>
                <path d="M12 8v4M12 16h.01" stroke="#d97706" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </span>
            <div className="gcStudent-resv-notice__body">
              <span className="gcStudent-resv-notice__heading">Important Notice</span>
              <p className="gcStudent-resv-notice__text">
                Please visit the library and collect your book within{" "}
                <strong>{expiryHours} hours</strong> of reservation. After that, your
                reservation will automatically expire.
              </p>
            </div>
          </div>

          <div className="gcStudent-resv-notice gcStudent-resv-notice--policy">
            <span className="gcStudent-resv-notice__icon">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 9v4M12 17h.01" stroke="#2563eb" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </span>
            <div className="gcStudent-resv-notice__body">
              <span className="gcStudent-resv-notice__heading">Reservation Policy</span>
              <ul className="gcStudent-resv-policy-list">
                {isTeacher ? (
                  <>
                    <li>A teacher can reserve up to {maxBooksStaff} books at one time.</li>
                    <li>Each issued book can be kept for a maximum of {maxIssueDays} days.</li>
                    <li>Books should be returned on or before the due date.</li>
                    <li>There is no overdue fine for teachers; however, timely return is expected.</li>
                    <li>Reserved books must be collected from the library after approval by the librarian.</li>
                    <li>Teachers can reserve another book only if they have not exceeded the maximum issue limit.</li>
                    <li>After the due date, the book should be returned to ensure fair access to library resources for all staff members.</li>
                  </>
                ) : (
                  <>
                    <li>Books are issued for a maximum period of {maxIssueDays} days.</li>
                    <li>A late return fine of Rs. {finePerDay} per day will be applied automatically after the due date.</li>
                    <li>A lost book fine of Rs. {lostBookFine} will be charged.</li>
                    <li>A damaged book fine of Rs. {damagedBookFine} will be charged.</li>
                    <li>Students can reserve up to {maxBooksStudent} books at one time.</li>
                    <li>Reserved books must be collected from the library within {expiryHours} hours; otherwise, the reservation will expire automatically and the book will become available for other users.</li>
                    <li>Book reservations require librarian approval before the book is officially issued.</li>
                    <li>After the due date, the book should be returned to ensure fair access to library resources for all students.</li>
                  </>
                )}
              </ul>
            </div>
          </div>

          <div className="gcStudent-resv-actions">
            <button
              className="gcStudent-resv-confirm-btn"
              onClick={handleConfirm}
              disabled={availableCopies === 0}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                <path d="M4 19.5A2.5 2.5 0 016.5 17H20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" stroke="currentColor" strokeWidth="1.8"/>
              </svg>
              {availableCopies === 0 ? "Not Available" : "Confirm Reservation"}
            </button>
            <button className="gcStudent-resv-cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ConfirmReservationPage;